import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../marketing/landing.css";
import "./login.css";
import { legacyAppUrl } from "../lib/legacyApp";
import { privatePilotSentence, useSignups } from "../lib/useSignups";
import { BrandMark } from "../ui/BrandMark";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";

/**
 * `/next` sign-in.
 *
 * WHY THIS EXISTS (the defect it fixes): the router shipped with exactly three routes — `/`,
 * `/cma`, `/result-demo` — while the landing nav linked to `/login` and `/signup`, and
 * `lib/auth.ts`'s 401 handler redirected to `/login`. With the SPA fallback rewrite serving
 * index.html for any unmatched path, every one of those landed on a router that matched nothing:
 * a blank page. Sign-in was not broken, it was absent.
 *
 * WHY IT CANNOT JUST LINK TO THE APEX SITE: the session is an httponly, host-only cookie with no
 * Domain attribute (`server/app.py::_set_cookie`, `samesite="lax"`). A cookie minted while the
 * browser is on valubrief.ai is never sent to next.valubrief.ai. The credential exchange has to
 * happen through THIS origin so the proxied Set-Cookie binds here — which it does, because the
 * Render rewrite `/api/* -> https://valubrief.ai/api/*` keeps the whole exchange same-origin.
 *
 * ADMIN 2FA: `POST /api/auth/login` does not always return a session. For an account in
 * VALUBRIEF_ADMIN_EMAILS, when MFA is live (SMTP configured or VALUBRIEF_ADMIN_MFA=1), it returns
 * `{mfa_required: true, email}` and mails a six-digit code instead — the session only exists after
 * `POST /api/auth/verify-otp`. A screen that assumed one round trip would hard-lock the owner out
 * of his own product the day SMTP is configured, so the code step is handled here from the start.
 *
 * No credential is stored, logged, or put in browser storage: the password lives in React state
 * for the duration of the submit and nothing else. Auth state is the server's cookie, exactly as
 * `lib/auth.ts` describes.
 */

type Stage = "credentials" | "code";

interface LoginOk {
  user?: { email?: string };
  mfa_required?: boolean;
  email?: string;
}

/**
 * UI-009: where to land after a successful sign-in. A user who was bounced out of `/cma/<jobId>` by
 * an expired session must come back to THAT job, not to a blank intake — a 34-second extraction
 * they already paid for is sitting behind that id.
 *
 * THE FALLBACK IS `/app`, NOT `/cma` (S03, 2026-09-22). It was `/cma` in all three branches below,
 * which meant signing in dropped every user straight into building a CMA/AMB whether or not that
 * was what they came for — no view of their own work, no CRM, no way back to a start. `/app` is
 * the signed-in front door (`home/AppHome.tsx`) and the builder is its primary action, one click
 * away. ONLY THE FALLBACK CHANGED: a valid same-origin `next` still wins, and the origin checks
 * below are untouched, because they are the security half of this function.
 *
 * Only a same-origin path is accepted, and the check is a real URL resolution rather than a string
 * test: `//evil.example` AND `/\evil.example` both resolve cross-origin (the browser normalizes the
 * backslash), and react-router falls back to `window.location.assign` when `pushState` rejects a
 * cross-origin URL — so a naive prefix check hands an attacker a phishing redirect that fires the
 * instant real credentials are accepted.
 */
/** Where a sign-in with no usable `next` lands. One constant, so the three refusal branches below
 *  cannot drift apart the way three string literals can. */
export const DEFAULT_SIGNED_IN_PATH = "/app";

export function safeNextPath(raw: string | null): string {
  if (!raw || !raw.startsWith("/")) return DEFAULT_SIGNED_IN_PATH;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://valubrief.invalid";
  try {
    const u = new URL(raw, origin);
    if (u.origin !== origin) return DEFAULT_SIGNED_IN_PATH;
    return u.pathname + u.search + u.hash;
  } catch {
    return DEFAULT_SIGNED_IN_PATH;
  }
}

export function LoginScreen() {
  useDocumentTitle(vbTitle("Sign in"));
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = safeNextPath(params.get("next"));
  const signups = useSignups();
  const [stage, setStage] = useState<Stage>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function post(path: string, body: unknown): Promise<LoginOk> {
    const res = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => null)) as LoginOk & { detail?: string };
    if (!res.ok) {
      // The server says "invalid credentials" for both a bad password and an unknown address, on
      // purpose (no account-enumeration oracle). Pass its wording through rather than guessing.
      throw new Error(data?.detail || "We couldn't sign you in. Check the email and password and try once more.");
    }
    return data || {};
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await post("/api/auth/login", { email: email.trim(), password });
      setPassword("");                       // never keep it in state past the exchange
      if (data.mfa_required) {
        setStage("code");
        setNotice(`A six-digit code is on its way to ${data.email || email.trim()}.`);
        return;
      }
      navigate(nextPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't sign you in. Check the email and password and try once more.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post("/api/auth/verify-otp", { email: email.trim(), code: code.trim() });
      navigate(nextPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn't match. Codes expire after a few minutes — go back and sign in again to get a fresh one.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vb-landing vb-auth">
      <div className="auth-card">
        <a className="auth-mark" href="/">
          {/* T-1 (2026-09-01): was `<img src={valubrief-logo.svg} className="mark-logo">`, the
              light navy lockup, which `ui/BrandMark` measures at 1.35:1 and 1.11:1 on the dark
              ground — absent, not dim. `BrandMark` emits both marks and lets `app.css`'s
              three-block cascade show exactly one. The height moves to `--vb-mark-h` in
              `login.css`, the same seam `marketing/sections.tsx` already uses, so the number
              stays in CSS and there is one way to set it rather than two. */}
          <BrandMark height={80} style={{ height: "var(--vb-mark-h, 80px)", width: "auto" }} />
        </a>

        {stage === "credentials" ? (
          <>
            <h1>Sign in</h1>
            <p className="auth-sub">For licensed real-estate professionals.</p>
            <form onSubmit={submitCredentials}>
              <label htmlFor="vb-email">Email</label>
              <input
                id="vb-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
              <label htmlFor="vb-password">Password</label>
              <input
                id="vb-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
              />
              {error ? <p className="auth-error" role="alert">{error}</p> : null}
              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1>Enter your code</h1>
            <p className="auth-sub">{notice}</p>
            <form onSubmit={submitCode}>
              <label htmlFor="vb-code">Six-digit code</label>
              <input
                id="vb-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(ev) => setCode(ev.target.value)}
              />
              {error ? <p className="auth-error" role="alert">{error}</p> : null}
              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Checking…" : "Continue"}
              </button>
              <button
                className="btn ghost"
                type="button"
                onClick={() => {
                  setStage("credentials");
                  setError(null);
                  setCode("");
                }}
              >
                Back
              </button>
            </form>
          </>
        )}

        {/* NO ACCOUNT YET (rewritten 2026-08-10). This line used to be a link into the legacy app,
            which — with sign-ups closed — walked someone into an app we are deprecating so it could
            tell them no. Now it says it here, in the API's own words, with the address to write to.

            The sentence and the contact address both come from `GET /api/signups`, so this page,
            the apex page and the 403 from /api/auth/signup cannot say three different things about
            who may create an account. Nothing renders until that answers: a one-paint delay on a
            footer line is invisible, whereas flashing "not accepting sign-ups" at a visitor and
            then changing your mind is not. */}
        {signups.status === "closed" && (
          <p className="auth-foot">{privatePilotSentence(signups.contact)}</p>
        )}
        {signups.status === "open" && (
          <p className="auth-foot">
            No account yet? <a href={legacyAppUrl()}>Request access</a>
          </p>
        )}
        {/* FORGOT PASSWORD (2026-09-04). Held back deliberately until mail worked: a link to a
            form that succeeds and delivers nothing is worse than no link, because it convinces
            the person a reset is coming. `/forgot` is honest either way — it says what was done,
            not what was found, and names the admin fallback that works regardless. */}
        <p className="auth-foot">
          <a href="/forgot">Forgot your password?</a>
        </p>
        {/* UI-040: an existing customer who forgot their password otherwise had no path (NAVIGATION rule 6).
            THE ADDRESS COMES FROM THE SERVER (2026-09-04). It was `hello@valubrief.ai`, hardcoded,
            three lines below a comment explaining that the sentence above it reads its address from
            `GET /api/signups` precisely so this page and the backend cannot say two different
            things. `_CONTACT_EMAIL` in app.py records that `contact@` was never actually created in
            the Zoho account it would have had to live in, and that the default is now the owner's
            real mailbox — whether `hello@` resolves anywhere was never tested. A locked-out user
            given a dead address has the same problem they started with, one layer deeper. Same
            fallback shape the sentence above uses: no address rather than a guessed one. */}
        <p className="auth-foot">
          {signups.contact ? (
            <>
              Trouble signing in?{" "}
              <a href={`mailto:${signups.contact}`}>{signups.contact}</a>
            </>
          ) : (
            <>Trouble signing in? Ask your ValuBrief administrator to send you a reset link.</>
          )}
        </p>
      </div>
    </div>
  );
}
